import { Controller } from '@hotwired/stimulus'
import { Turbo } from '@hotwired/turbo-rails'

// Comparador de ETFs con el filtrado del screener (ENH-RNK-044). Cada filtrado pide un turbo_stream
// que repinta el screener con morph (`index.turbo_stream.haml`) y deja la URL con los filtros
// elegidos, para poder compartirla, recargarla o volver atrás.
//
// La petición la hace este controlador y no Turbo: con Drive desactivado y la página dentro de
// `turbo-frame#content`, un envío del formulario recargaría la página entera (vuelta arriba, desplegables
// cerrados) en vez de aplicar el stream.
//
// Lo que está abierto vive en el propio formulario (este elemento), que el morph no toca: el stream solo
// repinta `#screener-etfs`, dentro. Son dos clases: el panel de filtros de móvil
// (`rnk-Screener-filtrosAbiertos`) y la rejilla de «Más filtros» de escritorio (`rnk-Screener-masFiltros`).
// También vive aquí el alto de la barra de resultados (`--screener-barra`), bajo la que se pega la
// cabecera de la tabla.
const ESPERA_AL_ESCRIBIR = 500
const CAMPOS_DE_TEXTO = 'input[type="text"], input[type="search"]'
// Lo que se deja libre entre un panel desplegado y el borde de la ventana.
const MARGEN_PANEL = 16
const FILTROS_ABIERTOS = 'rnk-Screener-filtrosAbiertos'
const MAS_FILTROS = 'rnk-Screener-masFiltros'
const SIN_SCROLL = 'rnk-Screener-sinScroll'
const CARGANDO = 'rnk-Screener-cargando'
const DESPLEGABLE_ABIERTO = '.rnk-Screener_Desplegable[open]'
const FOCALIZABLES = 'a[href], button, input, select, textarea, [tabindex]:not([tabindex="-1"])'

const normaliza = (texto) => texto.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()

// Lo que el tabulador puede alcanzar: ni lo escondido con `hidden` ni lo que el CSS no pinta. Lo mismo
// que resuelve `dialog-modal.js`.
const focalizable = (elemento) => !elemento.closest('[hidden]') && elemento.checkVisibility?.() !== false

export default class extends Controller {
  connect () {
    this.restaurarEstado = this.restaurarEstado.bind(this)
    document.addEventListener('turbo:before-stream-render', this.restaurarEstado)
    this.medirBarra()
  }

  disconnect () {
    clearTimeout(this.espera)
    this.peticion?.abort()
    this.observador?.disconnect()
    document.documentElement.classList.remove(SIN_SCROLL)
    document.removeEventListener('turbo:before-stream-render', this.restaurarEstado)
  }

  // Casillas, radios y el desplegable de orden: se filtra al momento.
  cambiar (event) {
    if (this.ignorado(event.target) || event.target.matches(CAMPOS_DE_TEXTO)) return

    this.enviar()
  }

  // Texto (TER, búsqueda): se espera a que se deje de escribir.
  escribir (event) {
    if (this.ignorado(event.target) || !event.target.matches(CAMPOS_DE_TEXTO)) return

    clearTimeout(this.espera)
    this.espera = setTimeout(() => this.enviar(), ESPERA_AL_ESCRIBIR)
  }

  // Presets de TER, «Quitar filtros» y «Mostrarlos»: enlaces que piden el mismo stream.
  enlace (event) {
    event.preventDefault()
    this.pedir(event.currentTarget.href)
  }

  // Móvil: una opción del menú de orden. El menú se cierra antes de pedir, para que el morph no lo reabra,
  // y el foco pasa a su botón: el enlace pulsado queda oculto y el foco se perdería.
  elegirOrden (event) {
    const menu = event.currentTarget.closest('details')
    menu.open = false
    menu.querySelector('summary').focus()
    this.enlace(event)
  }

  // Enter en el buscador o en el TER.
  alEnviar (event) {
    event.preventDefault()
    this.enviar()
  }

  // Los campos vacíos (TER sin límite, «Cualquiera», búsqueda en blanco) no viajan: no aportan nada
  // y ensuciarían la URL.
  enviar () {
    clearTimeout(this.espera)
    const parametros = new URLSearchParams()
    new FormData(this.element).forEach((valor, nombre) => {
      if (valor !== '') parametros.append(nombre, valor)
    })
    const consulta = parametros.toString()
    this.pedir(consulta ? `${this.element.action}?${consulta}` : this.element.action)
  }

  // Una petición viva a la vez: al escribir rápido, la respuesta de una búsqueda vieja no puede pisar
  // a la nueva. Si algo falla, se carga la página entera con esos filtros en vez de quedarse a medias.
  async pedir (url) {
    window.history.replaceState(window.history.state, '', url)
    this.peticion?.abort()
    const peticion = new AbortController()
    this.peticion = peticion
    this.element.classList.add(CARGANDO)
    this.element.setAttribute('aria-busy', 'true')
    try {
      const respuesta = await fetch(url, { headers: { Accept: 'text/vnd.turbo-stream.html' }, signal: peticion.signal })
      if (!respuesta.ok) throw new Error(`HTTP ${respuesta.status}`)

      Turbo.renderStreamMessage(await respuesta.text())
    } catch (error) {
      if (error.name !== 'AbortError') window.location.assign(url)
    } finally {
      if (this.peticion === peticion) {
        this.element.classList.remove(CARGANDO)
        this.element.removeAttribute('aria-busy')
      }
    }
  }

  // Móvil: el panel de filtros a pantalla completa. La página de detrás no se desplaza mientras tanto.
  abrirFiltros () {
    this.element.classList.add(FILTROS_ABIERTOS)
    document.documentElement.classList.add(SIN_SCROLL)
    this.sincronizarBotones()
    this.element.querySelector('.rnk-Screener_FiltrosCab .rnk-Screener_Cerrar')?.focus()
  }

  cerrarFiltros () {
    if (!this.element.classList.contains(FILTROS_ABIERTOS)) return

    this.element.classList.remove(FILTROS_ABIERTOS)
    document.documentElement.classList.remove(SIN_SCROLL)
    this.sincronizarBotones()
    this.element.querySelector('.rnk-Screener_BotonFiltros')?.focus()
  }

  // Escritorio: «Más filtros» despliega debajo de la barra la rejilla con el resto de filtros, y la
  // pliega. Lo elegido en ella sigue aplicado aunque se pliegue: lo dice el número del botón.
  alternarMasFiltros () {
    this.cerrarDesplegables()
    this.element.classList.toggle(MAS_FILTROS)
    this.sincronizarBotones()
  }

  // Esc cierra lo más interior que haya abierto: el desplegable y, en móvil, el panel de filtros.
  escape () {
    if (this.element.querySelector(DESPLEGABLE_ABIERTO)) return this.cerrarDesplegables()

    this.cerrarFiltros()
  }

  // Dentro del panel de móvil, el tabulador no se sale: la página de detrás está tapada y el foco
  // desaparecería de la vista.
  atraparFoco (event) {
    if (event.key !== 'Tab' || !this.element.classList.contains(FILTROS_ABIERTOS)) return

    const panel = this.element.querySelector('.rnk-Screener_Filtros')
    const focalizables = [...panel.querySelectorAll(FOCALIZABLES)].filter(focalizable)
    if (focalizables.length === 0) return

    const primero = focalizables[0]
    const ultimo = focalizables[focalizables.length - 1]
    const saliendo = event.shiftKey ? document.activeElement === primero : document.activeElement === ultimo
    if (!saliendo) return

    event.preventDefault()
    ;(event.shiftKey ? ultimo : primero).focus()
  }

  sincronizarBotones () {
    const abierto = this.element.classList.contains(FILTROS_ABIERTOS)
    this.element.querySelector('.rnk-Screener_BotonFiltros')?.setAttribute('aria-expanded', String(abierto))
    const masFiltros = this.element.classList.contains(MAS_FILTROS)
    this.element.querySelector('.rnk-Screener_MasFiltros')?.setAttribute('aria-expanded', String(masFiltros))
  }

  // Un desplegable abierto a la vez: los paneles se abren hacia abajo y dos se solaparían. El atributo
  // `name` ya lo hace en los navegadores con acordeón exclusivo; esto cubre los demás. Si el panel no
  // cabe por la derecha (la última columna), se alinea al borde derecho del botón.
  alternar (event) {
    const abierto = event.currentTarget
    if (!abierto.open) return

    this.element.querySelectorAll(DESPLEGABLE_ABIERTO).forEach((otro) => {
      if (otro !== abierto) otro.open = false
    })
    const panel = abierto.querySelector('.rnk-Screener_DesplegablePanel')
    panel.classList.remove('rnk-Screener_DesplegablePanel-derecha')
    if (panel.getBoundingClientRect().right > document.documentElement.clientWidth - MARGEN_PANEL) {
      panel.classList.add('rnk-Screener_DesplegablePanel-derecha')
    }
  }

  cerrarFuera (event) {
    if (event.target.closest('.rnk-Screener_Desplegable')) return

    this.cerrarDesplegables()
  }

  cerrarDesplegables () {
    this.element.querySelectorAll(DESPLEGABLE_ABIERTO).forEach((desplegable) => { desplegable.open = false })
  }

  // El buscador de una lista larga (gestoras, bolsas): oculta las opciones que no casan, sin pedir nada.
  filtrarOpciones (event) {
    const buscado = normaliza(event.target.value.trim())
    const filtro = event.target.closest('.rnk-Screener_Desplegable')
    filtro.querySelectorAll('[data-opcion]').forEach((opcion) => {
      opcion.hidden = buscado !== '' && !normaliza(opcion.dataset.opcion).includes(buscado)
    })
  }

  // El morph deja lo repintado como lo pinta el servidor: los desplegables cerrados y los botones
  // «Filtros» y «Más filtros» con el `aria-expanded` de la carga. Se reabre lo que estaba abierto, para
  // elegir varias opciones seguidas sin abrirlo cada vez, y los botones vuelven a decir lo que hay.
  restaurarEstado (event) {
    const abiertos = [...this.element.querySelectorAll('details[open]')].map((desplegable) => desplegable.id)
    const renderOriginal = event.detail.render
    event.detail.render = async (stream) => {
      await renderOriginal(stream)
      abiertos.forEach((id) => document.getElementById(id)?.setAttribute('open', ''))
      this.sincronizarBotones()
      this.medirBarra()
    }
  }

  // La barra cambia de alto con el ancho de la ventana. Si el morph la sustituye, se vuelve a observar
  // la nueva.
  medirBarra () {
    const barra = this.element.querySelector('.rnk-Screener_Barra')
    if (!barra || barra === this.barra) return

    this.barra = barra
    this.observador ??= new ResizeObserver(([entrada]) => {
      this.element.style.setProperty('--screener-barra', `${entrada.target.offsetHeight}px`)
    })
    this.observador.disconnect()
    this.observador.observe(barra)
  }

  ignorado (elemento) {
    return elemento.dataset.screenerEtfsIgnorar !== undefined
  }
}
